import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { fetchHolidayPromotions } from "../services/api";
import { bookingUrlWithFromRsSite } from "../utils/bookingFromSiteUrl";
import "./HolidayPromoModal.css";

const storageKey = () => `holiday_popup_dismissed_${new Date().toDateString()}`;

function hasBanner(p) {
  const u = p?.icon_url;
  if (u == null) return false;
  return String(u).trim().length > 0;
}

/** Попап лише на головній (вхід на сайт), не на success / tracks / тощо. */
function shouldShowHolidayPopup(pathname) {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  return p === "/";
}

/**
 * Попап акцій: банер (icon_url), CTA, «Не показувати».
 * Дані тягнемо через fetchHolidayPromotions — той самий fetch-ланцюжок, що fetchTracks у api.js.
 */
export default function HolidayPromoModal() {
  const location = useLocation();
  const [promos, setPromos] = useState([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  const current = useMemo(() => promos[index] ?? null, [promos, index]);

  useEffect(() => {
    const ac = new AbortController();

    fetchHolidayPromotions({ signal: ac.signal })
      .then((data) => {
        if (!Array.isArray(data)) {
          if (process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console.warn("[HolidayPromoModal] Очікувався масив, отримано:", typeof data, data);
          }
          return;
        }
        const withBanner = data.filter(hasBanner);
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.info("[HolidayPromoModal] акцій:", data.length, "з icon_url:", withBanner.length);
        }
        if (withBanner.length === 0) {
          if (process.env.NODE_ENV === "development" && data.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(
              "[HolidayPromoModal] Акції є, але без icon_url — завантаж банер у Filament (поле зображення попапу).",
            );
          }
          return;
        }
        setPromos(withBanner);
        setIndex(0);
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("[HolidayPromoModal] fetchHolidayPromotions:", e?.message || e);
        }
      });

    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (!promos.length) {
      setVisible(false);
      return;
    }
    if (sessionStorage.getItem(storageKey())) {
      setVisible(false);
      return;
    }
    if (!shouldShowHolidayPopup(location.pathname)) {
      setVisible(false);
      return;
    }
    setVisible(true);
  }, [promos, location.pathname]);

  if (!visible || !current || !hasBanner(current)) {
    return null;
  }

  const close = () => setVisible(false);

  const modal = (
    <div
      className="holiday-promo-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={current.name}
      data-holiday-popup="banner-v2"
      onClick={close}
    >
      <div className="holiday-promo-backdrop" aria-hidden="true" />
      <div className="holiday-promo-modal holiday-promo-modal--banner" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="holiday-promo-close" aria-label="Закрити" onClick={close}>
          ✕
        </button>

        <div className="holiday-promo-banner-wrap">
          <img src={current.icon_url} alt="" className="holiday-promo-hero" />
          <div className="holiday-promo-banner-gradient" aria-hidden="true" />
          <div className="holiday-promo-cta-overlay">
            <a href={bookingUrlWithFromRsSite()} className="holiday-promo-cta">
              Забронювати зараз
            </a>
          </div>
          <div className="holiday-promo-dismiss-wrap holiday-promo-dismiss-wrap--desktop">
            <button
              type="button"
              className="holiday-promo-dismiss"
              onClick={() => {
                sessionStorage.setItem(storageKey(), "1");
                setVisible(false);
              }}
            >
              Не показувати сьогодні
            </button>
          </div>
        </div>

        {promos.length > 1 && (
          <div className="holiday-promo-nav">
            <button type="button" disabled={index === 0} onClick={() => setIndex((i) => Math.max(0, i - 1))}>
              ←
            </button>
            <span>
              {index + 1} / {promos.length}
            </span>
            <button
              type="button"
              disabled={index >= promos.length - 1}
              onClick={() => setIndex((i) => Math.min(promos.length - 1, i + 1))}
            >
              →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : null;
}
